import { useState, useRef, useEffect } from 'react';
import { Text, Button, Badge, Input, Spinner, Card } from '@fluentui/react-components';
import { ArrowLeftRegular, SendRegular, ArrowDownloadRegular, ChatRegular, DeleteRegular } from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Project } from '../services/project-service';
import { useApiToken } from '../hooks/useApiToken';
import { apiUrl } from '../utils/api';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

interface AssessmentChatPageProps {
  project: Project;
  onBack: () => void;
}

const DOMAIN_LABELS: Record<string, string> = {
  'business-alignment': 'Business Alignment',
  'current-state': 'Current State',
  'cloud-readiness': 'Cloud Readiness',
  'technical-assessment': 'Technical Assessment',
  'security-compliance': 'Security & Compliance',
  'operations': 'Operations',
  'financial': 'Financial',
};

export function AssessmentChatPage({ project, onBack }: AssessmentChatPageProps) {
  const getToken = useApiToken();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [currentDomain, setCurrentDomain] = useState('');
  const [completedDomains, setCompletedDomains] = useState<string[]>([]);
  const [findingsCount, setFindingsCount] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Show welcome on mount — user initiates the conversation
  useEffect(() => {
    setMessages([{
      role: 'system',
      content: `CASE assessment for **${project.name}**. Type a message to begin — e.g. "Start the assessment" or answer the agent's questions directly.`,
    }]);
  }, [project.name]);

  const handleReset = async () => {
    if (!confirm('Reset this assessment? All chat history and findings will be deleted.')) return;
    try {
      const token = await getToken();
      // Delete the assessment document
      await fetch(apiUrl(`/api/documents/case-${project.id}?customerId=${encodeURIComponent(project.customerId)}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      // Clear local state
      setMessages([{
        role: 'system',
        content: `Assessment reset. Type a message to begin a new assessment.`,
      }]);
      setCurrentDomain('');
      setCompletedDomains([]);
      setFindingsCount(0);
    } catch (err) {
      console.error('Reset failed:', err);
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    const userMsg: ChatMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setSending(true);

    // Add placeholder assistant message that we'll stream into
    const assistantIdx = messages.length + 1; // +1 for the user message we just added
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString() }]);

    try {
      const token = await getToken();

      // Try SSE streaming endpoint first
      const res = await fetch(apiUrl(`/api/stream/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text }),
      });

      if (res.ok && res.headers.get('content-type')?.includes('text/event-stream')) {
        // Stream SSE tokens
        const reader = res.body?.getReader();
        const decoder = new TextDecoder();
        let accumulated = '';

        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6);
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.text) {
                    accumulated += parsed.text;
                    setMessages(prev => {
                      const updated = [...prev];
                      if (updated[assistantIdx]) {
                        updated[assistantIdx] = { ...updated[assistantIdx], content: accumulated };
                      }
                      return updated;
                    });
                  }
                  if (parsed.currentDomain) setCurrentDomain(parsed.currentDomain);
                  if (parsed.completedDomains) setCompletedDomains(parsed.completedDomains);
                  if (parsed.findingsCount !== undefined) setFindingsCount(parsed.findingsCount);
                  if (parsed.error) throw new Error(parsed.error);
                } catch { /* skip unparseable lines */ }
              }
            }
          }
        }
      } else {
        // Fallback to non-streaming endpoint
        const fallbackRes = await fetch(apiUrl(`/api/assessment/${project.id}/chat?customerId=${encodeURIComponent(project.customerId)}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: text }),
        });
        if (!fallbackRes.ok) throw new Error(`API error: ${fallbackRes.status}`);
        const data = await fallbackRes.json();
        setMessages(prev => {
          const updated = [...prev];
          if (updated[assistantIdx]) {
            updated[assistantIdx] = { ...updated[assistantIdx], content: data.reply };
          }
          return updated;
        });
        if (data.currentDomain) setCurrentDomain(data.currentDomain);
        if (data.completedDomains) setCompletedDomains(data.completedDomains);
        if (data.findingsCount !== undefined) setFindingsCount(data.findingsCount);
      }
    } catch (err) {
      setMessages(prev => [...prev, { role: 'system', content: `Error: ${err instanceof Error ? err.message : 'Unknown error'}` }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const handleExportCsv = async () => {
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/assessment/${project.id}/case-csv?customerId=${encodeURIComponent(project.customerId)}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CASE_Assessment_${project.name.replace(/\s/g, '_')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <div className="page-root assessment-chat-page">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title="Back" />
        <ChatRegular className="icon-title" />
        <Text size={500} weight="semibold">CASE Assessment</Text>
        <div className="title-bar-actions">
          {currentDomain && <Badge size="small" color="brand">{DOMAIN_LABELS[currentDomain] ?? currentDomain}</Badge>}
          <Badge size="small" appearance="outline">{completedDomains.length}/7 domains</Badge>
          <Badge size="small" appearance="outline">{findingsCount} findings</Badge>
          <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={handleExportCsv} title="Export CSV" />
          <Button appearance="subtle" icon={<DeleteRegular />} onClick={handleReset} title="Reset assessment" />
        </div>
      </div>

      <div className="assessment-domains-bar">
        {Object.entries(DOMAIN_LABELS).map(([key, label]) => (
          <Badge
            key={key}
            size="small"
            color={completedDomains.includes(key) ? 'success' : key === currentDomain ? 'brand' : 'informative'}
            appearance={completedDomains.includes(key) || key === currentDomain ? 'filled' : 'outline'}
          >
            {label}
          </Badge>
        ))}
      </div>

      <div className="chat-messages" ref={scrollRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`chat-message chat-message--${msg.role}`}>
            {msg.role === 'system' ? (
              <Card className="chat-system-card">
                <Text size={200} className="text-muted">{msg.content}</Text>
              </Card>
            ) : (
              <div className="chat-bubble">
                <div className="chat-bubble-header">
                  <Text size={200} weight="semibold">{msg.role === 'user' ? 'You' : 'CASE Agent'}</Text>
                </div>
                <div className="chat-bubble-content">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="chat-message chat-message--assistant">
            <div className="chat-bubble">
              <Spinner size="tiny" label="Thinking..." />
            </div>
          </div>
        )}
      </div>

      <div className="chat-input-bar">
        <Input
          className="chat-input"
          placeholder="Answer the assessment question..."
          value={input}
          onChange={(_e, d) => setInput(d.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />
        <Button
          appearance="primary"
          icon={<SendRegular />}
          onClick={() => sendMessage(input)}
          disabled={sending || !input.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
}
